import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { AnatomyFigure, MusclePreview } from './AnatomyFigure';
import {
  LEGEND,
  MUSCLES,
  mergePendingExercises,
  readPendingExercises,
} from '../../data/muscleExplorerData';
import './MuscleExplorer.css';

const NAV = [
  { to: '/muscles', label: 'Muscle Map', icon: PersonIcon },
  { to: '/workout', label: 'Exercises', icon: DumbbellIcon },
  { to: '/workout?tab=session', label: 'Workouts', icon: ClipboardIcon },
  { to: '/progress', label: 'Progress', icon: ChartIcon },
  { to: '/nutrition', label: 'Nutrition', icon: AppleIcon },
  { to: '/settings', label: 'Goals', icon: TargetIcon },
];

export default function MuscleExplorer() {
  const navigate = useNavigate();
  const [selectedId, setSelectedId] = useState('biceps');
  const [hoveredId, setHoveredId] = useState(null);
  const [focus, setFocus] = useState('front');
  const [pendingCount, setPendingCount] = useState(0);
  const [isNarrow, setIsNarrow] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 720 : false
  );

  useEffect(() => {
    setPendingCount(readPendingExercises().length);
    const onResize = () => setIsNarrow(window.innerWidth <= 720);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const muscle = MUSCLES[selectedId] || MUSCLES.biceps;

  useEffect(() => {
    if (muscle?.view) setFocus(muscle.view);
  }, [muscle?.view]);

  const addExercises = (list, { go } = {}) => {
    const merged = mergePendingExercises(list);
    setPendingCount(merged.length);
    const added = list.length === 1 ? list[0].name : `${list.length} exercises`;
    toast.success(`${added} added to workout`, {
      duration: 2500,
    });
    if (go) navigate('/workout?tab=session');
  };

  const handleSelect = (id) => {
    setSelectedId(id);
  };

  const handleLegend = (item) => {
    setHoveredId(null);
    setSelectedId(item.select);
    setFocus(MUSCLES[item.select]?.view || 'front');
  };

  const singleMode = isNarrow;

  const legendActive = useMemo(() => {
    const match = LEGEND.find(l => l.highlight.includes(selectedId));
    return match?.id;
  }, [selectedId]);

  return (
    <div className="me-page">
      <div className="me-inner">
        <header className="me-header">
          <Link to="/dashboard" className="me-brand" aria-label="Back to dashboard">
            <AlchemyMark />
            <div>
              <div className="me-brand-name">STACKED ALCHEMIST</div>
              <div className="me-brand-tag">Train today. Build tomorrow.</div>
            </div>
          </Link>

          <div className="me-title-wrap">
            <h1 className="me-title">MUSCLE EXPLORER</h1>
            <p className="me-subtitle">Select a muscle group to learn, exercise, and build.</p>
          </div>

          <div className="me-quote-header">
            <p>STRONGER PEOPLE.<br />BUILD BRIGHTER FUTURES.</p>
            <span>— STACKED ALCHEMIST</span>
          </div>
        </header>

        <div className="me-body">
          {/* Left */}
          <aside className="me-col me-col-left">
            <nav className="me-card me-nav" aria-label="Explorer">
              {NAV.map(item => {
                const active = item.to === '/muscles';
                const Icon = item.icon;
                return (
                  <Link
                    key={item.label}
                    to={item.to}
                    className={`me-nav-btn${active ? ' is-active' : ''}`}
                  >
                    <Icon />
                    {item.label}
                    {item.label === 'Workouts' && pendingCount > 0 && (
                      <span style={{
                        marginLeft: 'auto',
                        background: 'var(--me-accent)',
                        color: '#06202a',
                        borderRadius: 999,
                        fontSize: 10,
                        fontWeight: 700,
                        padding: '1px 6px',
                      }}>{pendingCount}</span>
                    )}
                  </Link>
                );
              })}
            </nav>

            <div className="me-card me-quote-card">
              <blockquote>“A stronger you builds a better tomorrow.”</blockquote>
              <cite>— STACKED ALCHEMIST</cite>
            </div>

            <div className="me-card me-legend">
              <h3>MUSCLE GROUPS</h3>
              {LEGEND.map(item => (
                <button
                  key={item.id}
                  type="button"
                  className={`me-legend-item${legendActive === item.id ? ' is-active' : ''}`}
                  onClick={() => handleLegend(item)}
                >
                  <span className="me-swatch" style={{ background: item.color, '--swatch': item.color }} />
                  {item.label}
                </button>
              ))}
            </div>
          </aside>

          {/* Center */}
          <section className="me-stage">
            <div className="me-toggles" role="tablist" aria-label="Anatomy view">
              <button
                type="button"
                className={`me-toggle${focus === 'front' ? ' is-active' : ''}`}
                onClick={() => setFocus('front')}
              >
                FRONT
              </button>
              <button
                type="button"
                className={`me-toggle${focus === 'back' ? ' is-active' : ''}`}
                onClick={() => setFocus('back')}
              >
                BACK
              </button>
            </div>

            <div className={`me-figures${singleMode ? ' single' : ''}`}>
              <AnatomyFigure
                side="front"
                selectedId={selectedId}
                hoveredId={hoveredId}
                onSelect={handleSelect}
                onHover={setHoveredId}
                dimmed={focus !== 'front'}
                focused={focus === 'front'}
              />
              <AnatomyFigure
                side="back"
                selectedId={selectedId}
                hoveredId={hoveredId}
                onSelect={handleSelect}
                onHover={setHoveredId}
                dimmed={focus !== 'back'}
                focused={focus === 'back'}
              />
            </div>

            <p className="me-hint">
              <PointerIcon />
              Click a muscle group to see details, exercises, and tips.
            </p>
          </section>

          {/* Right */}
          <aside className="me-col">
            <div className="me-card me-detail">
              <AnimatePresence mode="wait">
                <motion.div
                  key={muscle.id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.22 }}
                  style={{ display: 'flex', flexDirection: 'column', gap: '0.95rem', minHeight: 0 }}
                >
                  <div className="me-detail-head">
                    <div>
                      <div className="me-detail-kicker">
                        <span className="me-dot" style={{ background: muscle.color, '--swatch': muscle.color }} />
                        {muscle.group}
                      </div>
                      <h2>{muscle.name}</h2>
                    </div>
                    <MusclePreview muscle={muscle} />
                  </div>

                  <p className="me-desc">{muscle.description}</p>

                  <div>
                    <h3 className="me-section-label">KEY FUNCTIONS</h3>
                    <ul className="me-functions">
                      {muscle.functions.map(fn => (
                        <li key={fn}><span />{fn}</li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <div className="me-ex-head">
                      <h3 className="me-section-label" style={{ margin: 0 }}>TOP EXERCISES</h3>
                      <button
                        type="button"
                        className="me-link"
                        onClick={() => navigate(`/workout?bodyPart=${encodeURIComponent(muscle.bodyPartFilter)}`)}
                      >
                        View All →
                      </button>
                    </div>
                    <div className="me-ex-list" style={{ marginTop: 10 }}>
                      {muscle.exercises.map(ex => (
                        <button
                          key={ex.id}
                          type="button"
                          className="me-ex"
                          onClick={() => addExercises([ex])}
                          title="Add to workout"
                        >
                          <div className="me-ex-thumb"><LiftIcon /></div>
                          <div className="me-ex-meta">
                            <strong>{ex.name}</strong>
                            <small>{ex.type}</small>
                          </div>
                          <span className="me-ex-chevron">›</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="me-section-label">💡 PRO TIPS</h3>
                    <ul className="me-tips">
                      {muscle.tips.map(tip => <li key={tip}>{tip}</li>)}
                    </ul>
                  </div>

                  <button
                    type="button"
                    className="me-add"
                    onClick={() => addExercises(muscle.exercises, { go: true })}
                  >
                    <PlusIcon /> ADD TO WORKOUT
                  </button>
                </motion.div>
              </AnimatePresence>
            </div>
          </aside>
        </div>

        <footer className="me-footer">
          <div className="me-footer-brand">
            STACKED ALCHEMIST
            <small>Build a stronger you. Build a bigger tomorrow.</small>
          </div>
          <div className="me-footer-words">
            <span>FITNESS</span>
            <span>FOCUS</span>
            <span>DISCIPLINE</span>
            <span>FREEDOM</span>
          </div>
        </footer>
      </div>
    </div>
  );
}

function AlchemyMark() {
  return (
    <svg className="me-brand-mark" viewBox="0 0 48 48" fill="none" aria-hidden="true">
      <path d="M24 4 L44 40 H4 Z" stroke="#3ad4f6" strokeWidth="2.2" fill="rgba(58,212,246,0.06)" />
      <path d="M24 16 L36 38 H12 Z" stroke="#7ee7ff" strokeWidth="1.6" />
      <path d="M14 32 H34" stroke="#3ad4f6" strokeWidth="1.6" />
      <circle cx="24" cy="26" r="2.2" fill="#3ad4f6" />
    </svg>
  );
}

function PersonIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <circle cx="12" cy="7" r="3" />
      <path d="M5 20c1.2-4 4-6 7-6s5.8 2 7 6" strokeLinecap="round" />
    </svg>
  );
}
function DumbbellIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
      <path d="M6 9v6M8 8v8M16 8v8M18 9v6M8 12h8" />
    </svg>
  );
}
function ClipboardIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <rect x="6" y="5" width="12" height="15" rx="2" />
      <path d="M9 5V4h6v1M8 10h8M8 14h6" strokeLinecap="round" />
    </svg>
  );
}
function ChartIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
      <path d="M5 19V10M10 19V5M15 19v-7M20 19V8" />
    </svg>
  );
}
function AppleIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M12 7c2-3 5-3 5-3M12 21c-5 0-7-4-7-8 0-4 3-7 5.5-7 1.2 0 2 .5 2.5 1 .5-.5 1.3-1 2.5-1C18 6 21 9 21 13c0 4-2 8-9 8Z" />
    </svg>
  );
}
function TargetIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <circle cx="12" cy="12" r="8" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="12" cy="12" r="1.2" fill="currentColor" />
    </svg>
  );
}
function PointerIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor">
      <path d="M10 2c.6 0 1 .4 1 1v9.2l1.2-.6c.7-.4 1.6 0 1.8.8l.3 1.2 2.3-1.1c.8-.4 1.7.2 1.8 1.1l.4 3.2c.1 1-.4 2-1.3 2.5L12 23l-5.2-8.4c-.5-.8 0-1.8.9-1.8H10V3c0-.6.4-1 1-1Z" />
    </svg>
  );
}
function PlusIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
      <path d="M12 5v14M5 12h14" strokeLinecap="round" />
    </svg>
  );
}
function LiftIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
      <circle cx="12" cy="5.5" r="2" />
      <path d="M8 10h8l-1.5 5H9.5L8 10Z" />
      <path d="M4 9.5h3M17 9.5h3M5.5 8v3M18.5 8v3" />
      <path d="M10 15.5 9 21M14 15.5l1 5.5" />
    </svg>
  );
}
